import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import ClassNames from 'classnames';

import Select from '../Form/Select';
import Input from '../Form/Input';
import DatesFromTo from '../Form/DatesFromTo';

import AdvancedFilters from './AdvancedFilters';

import Accordion from '../Accordion';
import ToggleOpenClose from '../ToggleOpenClose';
import useViewUpdate from '../../../managers/hooks/useViewUpdate';
import ViewManager from '../../../managers/view';
import { mobileLargeBreakpoint, tabletBreakpoint } from '../../../constants/breakpoints';

import { StoreContext } from './store/StoreContext';
import { GlobalsContext } from './context/globals';

const Filters = ({ onSearch, onReset, onUpdate }) => {
  const [isMobileAccordionOpen, setIsMobileAccordionOpen] = useState(true);
  const [mobileAccordionCacheKey, setMobileAccordionCacheKey] = useState(new Date().getTime());
  const [hasSearched, setHasSearched] = useState(false);
  const { state } = useContext(StoreContext);
  const {
    globals: { labels, radiusOptions },
  } = useContext(GlobalsContext);

  const onSearchClick = (e) => {
    e.preventDefault();
    onSearch();
    setHasSearched(true);
  };

  // Reopen mobile accordion if closed upon transition to above mobile breakpoint
  useViewUpdate(() => {
    if (ViewManager.hasTransitionedAbove(mobileLargeBreakpoint) && !isMobileAccordionOpen) {
      setIsMobileAccordionOpen(true);
      // utilize reset of key to prompt take up of isMobileAccordionOpen;
      setMobileAccordionCacheKey(new Date().getTime());
    }
  });

  // Once first search has taken place, collapse search form on mobile
  useEffect(() => {
    if (hasSearched === true && ViewManager.isBelow(tabletBreakpoint)) {
      setTimeout(() => {
        setIsMobileAccordionOpen(false);
      }, 1000); // brief delay to continue to display filters before collapse
    }
  }, [hasSearched]);

  const classNames = ClassNames({
    WhatsOnFilters: true,
    'WhatsOnFilters-hasSearched': hasSearched,
    'WhatsOnFilters-isLoading': state.isLoading,
  });

  const searchButtonClassNames = ClassNames({
    WhatsOnFilters_searchButton: true,
    Button: true,
    'Button-loading': state.isLoading,
  });

  const getSearchSubmitButtonTitle = () => {
    if (state.location === '' || state.location === null) {
      return 'Please enter a location';
    }
    return state.searchIsStale ? labels.staleSearchTitle : labels.buttonLabel;
  };

  const searchIsInvalid = () => {
    const locationIsEmpty = state.location === null || state.location === '';
    const radiusHasAValue = state.radiusKm > 0;
    return locationIsEmpty && radiusHasAValue;
  };

  return (
    <div className={classNames}>
      <h2 className="WhatsOnFilters_title">{labels.searchTitle}</h2>

      <Accordion
        key={mobileAccordionCacheKey}
        isOpen={isMobileAccordionOpen}
        onChange={setIsMobileAccordionOpen}
        toggleElement={({ onClose, onOpen, isOpen }) => (
          <ToggleOpenClose
            className="WhatsOnFilters_mobileOnlyToggleOpenClose"
            closeLabel={labels.mobileAccordionCloseLabel}
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            openLabel={labels.mobileAccordionOpenLabel}
          />
        )}
        drawerElement={({ drawerRef }) => (
          <form
            className="WhatsOnFilters_form Form Form-coloredBackground Form-noBorder"
            ref={drawerRef}
          >
            <div className="WhatsOnFilters_fieldsets">
              <fieldset className="WhatsOnFilters_fieldset Form_fieldset">
                <Input
                  disabled={state.isLoading}
                  id="keywords"
                  info={labels.keywordsInfo}
                  label={labels.keywordsLabel}
                  onChange={(ev) => onUpdate('keywords', ev.currentTarget.value)}
                  placeholder={labels.keywordsPlaceholder}
                  value={state.keywords || ''}
                />
              </fieldset>
              <fieldset className="WhatsOnFilters_fieldset Form_fieldset">
                <Input
                  disabled={state.isLoading}
                  id="location"
                  info={labels.locationInfo}
                  label={labels.locationLabel}
                  onChange={(ev) => onUpdate('location', ev.currentTarget.value)}
                  placeholder={labels.locationPlaceholder}
                  value={state.location || ''}
                  isInvalid={searchIsInvalid()}
                  invalidMessage="Please enter a location"
                />
              </fieldset>
              <fieldset className="WhatsOnFilters_fieldset Form_fieldset">
                <DatesFromTo
                  disabled={state.isLoading}
                  id="Dates"
                  fromPlaceholder={labels.dateFromPlaceholder}
                  fromDate={state.startDate}
                  info={labels.datesInfo}
                  label={labels.datesLabel}
                  onFromChange={(date) => onUpdate('startDate', date)}
                  onToChange={(date) => onUpdate('endDate', date)}
                  toPlaceholder={labels.dateToPlaceholder}
                  toDate={state.endDate}
                />
              </fieldset>
              <fieldset className="WhatsOnFilters_fieldset Form_fieldset">
                <Select
                  disabled={state.isLoading}
                  id="radius"
                  label={labels.radiusLabel}
                  info={labels.radiusInfo}
                  options={radiusOptions}
                  onChange={(ev) => onUpdate('radiusKm', parseInt(ev.currentTarget.value, 10))} // convert to int
                  value={state.radiusKm.toString()} // parse from int
                  invalidMessage="Please select a radius"
                />
              </fieldset>
            </div>

            <AdvancedFilters tags={state.tags || []} onChange={(t) => onUpdate('tags', t)} />

            <footer className="WhatsOnFilters_footer">
              <button
                type="button"
                className="WhatsOnFilters_resetButton Button Button-transparentForm"
                onClick={onReset}
                disabled={state.isLoading || state.resetIsStale}
                title={state.resetIsStale ? labels.staleResetTitle : labels.resetButtonLabel}
              >
                {labels.resetButtonLabel}
              </button>

              <button
                type="submit"
                className={searchButtonClassNames}
                disabled={state.searchIsStale || searchIsInvalid()}
                onClick={onSearchClick}
                title={getSearchSubmitButtonTitle()}
              >
                {labels.buttonLabel}
                <div className="LoadingBeacon">
                  <div className="LoadingBeacon_loadingDiscs">
                    <div className="LoadingBeacon_loadingDisc"></div>
                    <div className="LoadingBeacon_loadingDisc"></div>
                    <div className="LoadingBeacon_loadingDisc"></div>
                  </div>
                </div>
              </button>
            </footer>
          </form>
        )}
      />
    </div>
  );
};

Filters.propTypes = {
  onUpdate: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
};

export default Filters;
